import { normalizeIdentity } from '@/shared/lib/normalize'
import type { RepoPatient } from '@/shared/repositories/patients/patientsRepository'
import type { ApiExamResult } from '@/shared/repositories/exams/examsRepository'
import type { ApiEvent } from '@/shared/repositories/events/eventsRepository'
import type { ApiDocument, ApiTemplate } from '@/shared/repositories/clinicalDocuments/clinicalDocumentsRepository'
import type { InventoryStockLine } from '@/shared/repositories/inventory/inventoryStockRepository'
import type { ExamTypeRecord } from '@/shared/repositories/examTypes/examTypesRepositoryUtils'
import type { LocationRecord } from '@/shared/repositories/locations/locationsRepository'
import type { AttendanceTypeRecord } from '@/shared/repositories/attendanceTypes/attendanceTypesRepository'
import type { CidCodeRecord } from '@/shared/repositories/cidCodes/cidCodesRepository'
import type { ProcedureCodeRecord } from '@/shared/repositories/procedureCodes/procedureCodesRepository'
import type { ActiveIngredientRecord } from '@/shared/repositories/activeIngredients/activeIngredientsRepository'
import type { PlanRecord, UserLicenseRecord, AdminSettingsRecord } from '@/shared/repositories/admin/adminLicensesRepository'
import type { AiUsageStatus } from '@/shared/repositories/aiUsage/aiUsageRepository'
import type { ShiftTimeEntryRecord } from '@/shared/repositories/shiftTime/shiftTimeEntriesRepository'
import type { ShiftLocationPeriodPaymentRecord } from '@/shared/repositories/shiftPayments/shiftLocationPeriodPaymentsRepository'
import type { Json } from '@/shared/supabase/database.types'

export const FIXTURE_USER_DOCTOR_ID = 'a0000000-0000-4000-8000-000000000001'
export const FIXTURE_USER_SYSTEM_ADMIN_ID = 'a0000000-0000-4000-8000-000000000002'

const pad = (n: number, w = 12) => String(n).padStart(w, '0')

export const fixturePatientId = (index1: number): string =>
  `b0000000-0000-4000-8000-${pad(index1, 12)}`

const nowIso = '2026-04-28T14:30:00.000Z'

const patientNames = [
  'Ana Beatriz Ferreira',
  'Bruno Costa Lima',
  'Carla Mendes Rocha',
  'Daniel Souza Alves',
  'Eduarda Pires Nunes',
  'Felipe Ramos Dias',
  'Gabriela Lopes Araújo',
  'Henrique Martins Cardoso',
  'Isabela Freitas Monteiro',
  'João Pedro Teixeira',
  'Karina Vieira Barros',
  'Lucas Nogueira Campos',
  'Mariana Duarte Ribeiro',
  'Nicolas Azevedo Pinto',
  'Olivia Cavalcanti Moura',
  'Paulo Henrique Sales',
  'Quésia Andrade Farias',
  'Rafael Borges Melo',
  'Sabrina Correia Lins',
  'Thiago Machado Peixoto',
]

export function screenshotFixturePatients(): RepoPatient[] {
  return patientNames.map((name, i) => {
    const id = fixturePatientId(i + 1)
    const created = new Date(2025, 5 + (i % 6), 10 + i).toISOString()
    return {
      id,
      name,
      normalizedName: normalizeIdentity(name),
      tags: i % 3 === 0 ? ['hipertensão', 'diabetes'] : i % 3 === 1 ? ['asma'] : ['acompanhamento'],
      documentNumber: `${100000000 + i * 137}`.slice(0, 11),
      email: `paciente.mock${i + 1}@exemplo.clinica.invalid`,
      sex: i % 2 === 0 ? 'F' : 'M',
      birthDate: `${1980 + (i % 25)}-${String((i % 12) + 1).padStart(2, '0')}-15`,
      age: 18 + (i % 40),
      city: ['São Paulo', 'Campinas', 'Curitiba', 'Belo Horizonte', 'Porto Alegre'][i % 5],
      summary: i % 4 === 0 ? 'Acompanhamento de condição crônica; exames periódicos.' : 'Consulta de rotina.',
      comment: i % 5 === 0 ? 'Paciente comparece pontualmente.' : '',
      attendanceTypeId: undefined,
      attendanceTypeName: i % 2 === 0 ? 'Particular' : 'Convênio',
      isGestatingOrLactating: i === 4 ? true : null,
      lastWeightKg: i % 5 === 0 ? 70 + i : undefined,
      lastWeightAt: i % 5 === 0 ? created : undefined,
      createdAt: created,
      updatedAt: created,
    }
  })
}

function addMonths(iso: string, months: number): string {
  const d = new Date(iso)
  d.setMonth(d.getMonth() + months)
  return d.toISOString()
}

/** Dezenas de resultados (glicemia, HbA1c, lipídios) para gráficos e tabelas. */
export function screenshotFixtureExamResults(patientIdFilter?: string): ApiExamResult[] {
  const patients = screenshotFixturePatients()
  const p0 = patients[0]!.id
  const p1 = patients[1]!.id
  const targets = patientIdFilter ? [patientIdFilter] : [p0, p1, patients[2]!.id]

  const rows: ApiExamResult[] = []
  let seq = 1

  const push = (r: Omit<ApiExamResult, 'id'> & { id?: string }) => {
    rows.push({
      ...r,
      id: r.id ?? `e0000000-0000-4000-8000-${pad(seq++, 12)}`,
    })
  }

  for (const pid of targets) {
    for (let m = 0; m < 8; m++) {
      const base = addMonths('2025-09-01T10:00:00.000Z', m)
      push({
        patientId: pid,
        examTypeId: 'glicemia',
        examName: 'Glicemia em jejum',
        normalizedExamName: 'glicemia',
        date: base,
        value: 82 + m * 4 + (pid === p0 ? 8 : 0),
        numericValue: 82 + m * 4 + (pid === p0 ? 8 : 0),
        unit: 'mg/dL',
        laboratory: 'Lab Synap Mock',
        refMin: 70,
        refMax: 99,
        createdAt: base,
      })
    }
    for (let m = 0; m < 5; m++) {
      const base = addMonths('2025-10-15T10:00:00.000Z', m * 2)
      push({
        patientId: pid,
        examTypeId: 'hemoglobina-glicada',
        examName: 'Hemoglobina glicada (HbA1c)',
        normalizedExamName: 'hemoglobina-glicada',
        date: base,
        value: 5.2 + m * 0.08,
        numericValue: 5.2 + m * 0.08,
        unit: '%',
        refMin: 4,
        refMax: 5.6,
        createdAt: base,
      })
    }
    push({
      patientId: pid,
      examTypeId: 'colesterol-total',
      examName: 'Colesterol total',
      normalizedExamName: 'colesterol-total',
      date: nowIso,
      value: 185 + (pid === p0 ? 15 : 0),
      numericValue: 185 + (pid === p0 ? 15 : 0),
      unit: 'mg/dL',
      refMin: 0,
      refMax: 190,
      createdAt: nowIso,
    })
    push({
      patientId: pid,
      examTypeId: 'ldl',
      examName: 'LDL',
      normalizedExamName: 'ldl',
      date: nowIso,
      value: 118,
      numericValue: 118,
      unit: 'mg/dL',
      refMin: 0,
      refMax: 100,
      warningThresholdPercent: 15,
      createdAt: nowIso,
    })
  }

  for (let i = 3; i < 20; i++) {
    const pid = patients[i]!.id
    push({
      patientId: pid,
      examTypeId: 'hemograma',
      examName: 'Hemograma completo',
      normalizedExamName: 'hemograma',
      date: addMonths(nowIso, -i),
      value: 'Leucócitos 7.200/mm³; sem alterações relevantes.',
      textValue: 'Leucócitos 7.200/mm³; sem alterações relevantes.',
      laboratory: 'Laboratório Central Mock',
      createdAt: addMonths(nowIso, -i),
    })
  }

  return rows
}

function screenshotFixtureEventsBundle(): {
  events: ApiEvent[]
  plantaoEventIds: {
    upaCompleted: string
    consultorioCompleted: string
    hospitalCompleted: string
    upaCompleted2: string
    upaOpen: string
  }
} {
  const patients = screenshotFixturePatients()
  const out: ApiEvent[] = []
  let n = 1
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  // Âncora em "hoje" para o smoke Playwright e a vista Dia da agenda sempre listarem eventos.
  for (let offset = -3; offset < 11; offset++) {
    const day = new Date(today)
    day.setDate(today.getDate() + offset)
    const start = new Date(day)
    start.setHours(9, 0, 0, 0)
    const end = new Date(start.getTime() + 45 * 60 * 1000)
    const slot = offset + 3
    out.push({
      id: `ev000000-0000-4000-8000-${pad(n++, 12)}`,
      title:
        slot % 2 === 0
          ? `Consulta — ${patients[slot % 20]!.name}`
          : `Retorno — ${patients[(slot + 3) % 20]!.name}`,
      description: slot % 3 === 0 ? 'Trazer exames em jejum.' : undefined,
      location: slot % 4 === 0 ? 'Consultório 2' : 'Consultório 1',
      locationId: slot % 4 === 0 ? 'loc-2' : slot % 3 === 0 ? 'loc-4' : undefined,
      startDateTime: start.toISOString(),
      endDateTime: end.toISOString(),
      allDay: false,
      patientId: patients[slot % 20]!.id,
      reminderEnabled: slot % 2 === 0,
      reminderMinutesBefore: 60,
      bookingStatus: slot % 5 === 0 ? 'pending' : 'confirmed',
      createdAt: start.toISOString(),
      updatedAt: start.toISOString(),
    })
  }
  const congressoDay = new Date(today)
  congressoDay.setDate(today.getDate() + 5)
  const congressoStart = new Date(congressoDay)
  congressoStart.setHours(0, 0, 0, 0)
  const congressoEnd = new Date(congressoDay)
  congressoEnd.setHours(23, 59, 0, 0)
  out.push({
    id: `ev000000-0000-4000-8000-${pad(n++, 12)}`,
    title: 'Congresso (bloqueio)',
    startDateTime: congressoStart.toISOString(),
    endDateTime: congressoEnd.toISOString(),
    allDay: true,
    createdAt: nowIso,
    updatedAt: nowIso,
  })

  const plantaoIds = {
    upaCompleted: `ev000000-0000-4000-8000-${pad(n++, 12)}`,
    consultorioCompleted: `ev000000-0000-4000-8000-${pad(n++, 12)}`,
    hospitalCompleted: `ev000000-0000-4000-8000-${pad(n++, 12)}`,
    upaCompleted2: `ev000000-0000-4000-8000-${pad(n++, 12)}`,
    upaOpen: `ev000000-0000-4000-8000-${pad(n++, 12)}`,
  }

  const daysAgo = (nDays: number) => {
    const d = new Date(today)
    d.setDate(today.getDate() - nDays)
    return d
  }

  const locationLabel = (locationId: string) => {
    if (locationId === 'loc-4') return 'UPA Bernard'
    if (locationId === 'loc-1') return 'Hospital Central'
    return 'Consultório Central'
  }

  const pushPlantao = (id: string, title: string, locationId: string, day: Date, hours: number) => {
    const start = new Date(day)
    start.setHours(7, 0, 0, 0)
    const end = new Date(start.getTime() + hours * 60 * 60 * 1000)
    out.push({
      id,
      title,
      locationId,
      location: locationLabel(locationId),
      startDateTime: start.toISOString(),
      endDateTime: end.toISOString(),
      allDay: false,
      createdAt: start.toISOString(),
      updatedAt: end.toISOString(),
    })
  }

  // Vários locais no mês corrente para o breakdown dos KPIs A receber / Recebido.
  pushPlantao(plantaoIds.upaCompleted, 'Plantão — UPA Bernard', 'loc-4', daysAgo(8), 12)
  pushPlantao(plantaoIds.upaCompleted2, 'Plantão — UPA Bernard (noite)', 'loc-4', daysAgo(5), 12)
  pushPlantao(plantaoIds.consultorioCompleted, 'Plantão — Consultório Central', 'loc-2', daysAgo(3), 6)
  pushPlantao(plantaoIds.hospitalCompleted, 'Plantão — Hospital Central', 'loc-1', daysAgo(2), 10)
  pushPlantao(plantaoIds.upaOpen, 'Plantão — UPA Bernard (em andamento)', 'loc-4', daysAgo(0), 4)

  return { events: out, plantaoEventIds: plantaoIds }
}

export function screenshotFixtureEvents(): ApiEvent[] {
  return screenshotFixtureEventsBundle().events
}

/** Registros de turno alinhados aos eventos de plantão do fixture. */
export function screenshotFixtureShiftTimeEntries(): ShiftTimeEntryRecord[] {
  const { plantaoEventIds } = screenshotFixtureEventsBundle()
  const uid = FIXTURE_USER_DOCTOR_ID
  const t = nowIso

  const daysAgoStart = (nDays: number, hour: number) => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    d.setDate(d.getDate() - nDays)
    d.setHours(hour, 0, 0, 0)
    return d.toISOString()
  }

  const daysAgoEnd = (startIso: string, hours: number) =>
    new Date(new Date(startIso).getTime() + hours * 60 * 60 * 1000).toISOString()

  const upaStart = daysAgoStart(8, 7)
  const upaStart2 = daysAgoStart(5, 19)
  const consultStart = daysAgoStart(3, 8)
  const hospitalStart = daysAgoStart(2, 7)
  const openStart = daysAgoStart(0, 7)

  return [
    {
      id: 'shift-001',
      ownerUserId: uid,
      eventId: plantaoEventIds.upaCompleted,
      locationId: 'loc-4',
      startedAt: upaStart,
      endedAt: daysAgoEnd(upaStart, 11.5),
      attendanceCount: 14,
      createdAt: t,
      updatedAt: t,
    },
    {
      id: 'shift-001b',
      ownerUserId: uid,
      eventId: plantaoEventIds.upaCompleted2,
      locationId: 'loc-4',
      startedAt: upaStart2,
      endedAt: daysAgoEnd(upaStart2, 12),
      attendanceCount: 9,
      createdAt: t,
      updatedAt: t,
    },
    {
      id: 'shift-002',
      ownerUserId: uid,
      eventId: plantaoEventIds.consultorioCompleted,
      locationId: 'loc-2',
      startedAt: consultStart,
      endedAt: daysAgoEnd(consultStart, 6),
      attendanceCount: null,
      createdAt: t,
      updatedAt: t,
    },
    {
      id: 'shift-004',
      ownerUserId: uid,
      eventId: plantaoEventIds.hospitalCompleted,
      locationId: 'loc-1',
      startedAt: hospitalStart,
      endedAt: daysAgoEnd(hospitalStart, 10),
      attendanceCount: null,
      createdAt: t,
      updatedAt: t,
    },
    {
      id: 'shift-003',
      ownerUserId: uid,
      eventId: plantaoEventIds.upaOpen,
      locationId: 'loc-4',
      startedAt: openStart,
      endedAt: null,
      attendanceCount: null,
      createdAt: t,
      updatedAt: t,
    },
  ]
}

/**
 * Status local×mês para KPIs em modo screenshot: Consultório Central marcado como pago
 * no mês corrente (e em qualquer `keys` pedida que bata com o mês de hoje).
 */
export function screenshotFixtureShiftLocationPeriodPayments(
  keys: string[],
): ShiftLocationPeriodPaymentRecord[] {
  if (keys.length === 0) return []
  const now = new Date()
  const year = now.getUTCFullYear()
  const month = now.getUTCMonth()
  const currentKey = `month:${year}-${String(month + 1).padStart(2, '0')}`
  if (!keys.includes(currentKey)) return []
  const periodStart = new Date(Date.UTC(year, month, 1)).toISOString()
  const periodEnd = new Date(Date.UTC(year, month + 1, 1) - 1).toISOString()
  const t = nowIso
  return [
    {
      id: 'fixture-pay-loc-2',
      ownerUserId: FIXTURE_USER_DOCTOR_ID,
      locationId: 'loc-2',
      periodKey: currentKey,
      periodStart,
      periodEnd,
      paid: true,
      paidAt: t,
      createdAt: t,
      updatedAt: t,
    },
  ]
}

export function screenshotFixtureTemplates(): ApiTemplate[] {
  const t = nowIso
  return [
    {
      id: 'tmpl-001',
      type: 'prescription',
      name: 'Receita simples',
      description: 'Modelo para medicação de uso contínuo.',
      createdAt: t,
      updatedAt: t,
    },
    {
      id: 'tmpl-002',
      type: 'certificate',
      name: 'Atestado médico',
      description: 'Dias de afastamento e CID.',
      createdAt: t,
      updatedAt: t,
    },
    {
      id: 'tmpl-003',
      type: 'request',
      name: 'Solicitação de exames',
      description: 'Painel de exames de rotina.',
      createdAt: t,
      updatedAt: t,
    },
  ]
}

export function screenshotFixtureDocuments(): ApiDocument[] {
  const patients = screenshotFixturePatients()
  const uid = FIXTURE_USER_DOCTOR_ID
  const t = nowIso
  const docs: ApiDocument[] = []
  let k = 1
  for (let i = 0; i < 12; i++) {
    const pid = patients[i % 20]!.id
    const type = (['prescription', 'certificate', 'request'] as const)[i % 3]!
    docs.push({
      id: `doc-${pad(k++, 4)}`,
      patientId: pid,
      type: i % 4 === 0 ? 'generic' : type,
      title:
        type === 'prescription'
          ? `Receita ${i + 1} — anti-hipertensivos`
          : type === 'certificate'
            ? `Atestado ${i + 1} — ${2 + (i % 5)} dias`
            : `Solicitação exames ${i + 1}`,
      summary: 'Documento gerado em modo demonstração.',
      content: '<p>Conteúdo de exemplo para captura de tela.</p>',
      details: '<p>Conteúdo de exemplo para captura de tela.</p>',
      templateId: i % 2 === 0 ? 'tmpl-001' : undefined,
      createdByUserId: uid,
      createdAt: addMonths(t, -i),
      updatedAt: addMonths(t, -i),
      documentDate: addMonths(t, -i).slice(0, 10),
    })
  }
  return docs
}

export function screenshotFixtureInventoryLines(): InventoryStockLine[] {
  const t = nowIso
  return [
    {
      id: 'inv-line-1',
      quantity: 24,
      patientId: null,
      patientName: null,
      productId: 'inv-prod-1',
      productName: 'Paracetamol 750 mg — compr.',
      productUnit: 'cx',
      productCategory: 'medication',
      productMinAlert: 5,
      activeIngredientId: null,
      activeIngredientName: null,
      minAlertOverride: null,
      effectiveMinAlert: 5,
      notes: 'Uso em consultório',
      updatedAt: t,
      isOut: false,
      isLow: false,
    },
    {
      id: 'inv-line-2',
      quantity: 2,
      patientId: fixturePatientId(1),
      patientName: patientNames[0] ?? 'Paciente',
      productId: 'inv-prod-2',
      productName: 'Soro fisiológico 0,9% 500 ml',
      productUnit: 'fr',
      productCategory: 'consumable',
      productMinAlert: 4,
      activeIngredientId: null,
      activeIngredientName: null,
      minAlertOverride: null,
      effectiveMinAlert: 4,
      notes: null,
      updatedAt: t,
      isOut: false,
      isLow: true,
    },
    {
      id: 'inv-line-3',
      quantity: 0,
      patientId: null,
      patientName: null,
      productId: 'inv-prod-3',
      productName: 'Máscara descartável PFF2',
      productUnit: 'un',
      productCategory: 'consumable',
      productMinAlert: 20,
      activeIngredientId: null,
      activeIngredientName: null,
      minAlertOverride: null,
      effectiveMinAlert: 20,
      notes: null,
      updatedAt: t,
      isOut: true,
      isLow: true,
    },
  ]
}

export function screenshotFixtureExamTypes(): ExamTypeRecord[] {
  const t = nowIso
  const names = ['Glicemia em jejum', 'Hemograma', 'TSH', 'Vitamina D', 'PCR ultrasensível', 'Urina tipo I']
  return names.map((examName, i) => ({
    id: `ext-${pad(i + 1, 8)}`,
    examName,
    normalizedExamName: normalizeIdentity(examName),
    createdAt: t,
    updatedAt: t,
  }))
}

export function screenshotFixtureLocations(): LocationRecord[] {
  const t = nowIso
  const base = {
    groupId: null as string | null,
    groupName: null as string | null,
    isRemunerated: false as const,
    hourlyRateCents: null,
    estimatedPaymentDay: null,
    defaultShiftStartTime: null,
    defaultEstimatedHours: null,
    countsAttendances: false,
    createdAt: t,
    updatedAt: t,
  }
  return [
    {
      id: 'loc-1',
      name: 'Hospital Central',
      address: 'Av. Paulista, 1000',
      category: 'hospital',
      groupId: null,
      groupName: null,
      isRemunerated: true,
      hourlyRateCents: 22000,
      estimatedPaymentDay: 15,
      defaultShiftStartTime: '07:00:00',
      defaultEstimatedHours: 10,
      countsAttendances: false,
      createdAt: t,
      updatedAt: t,
    },
    {
      id: 'loc-2',
      name: 'Consultório Central',
      address: 'Rua das Flores, 42',
      category: 'consultorio',
      groupId: null,
      groupName: null,
      isRemunerated: true,
      hourlyRateCents: 18000,
      estimatedPaymentDay: 5,
      defaultShiftStartTime: '07:00:00',
      defaultEstimatedHours: 6,
      countsAttendances: false,
      createdAt: t,
      updatedAt: t,
    },
    { id: 'loc-3', name: 'Unidade genérica', address: null, category: 'generico', ...base },
    {
      id: 'loc-4',
      name: 'UPA Bernard',
      address: 'Rod. Anhanguera, km 12',
      category: 'hospital',
      groupId: null,
      groupName: null,
      isRemunerated: true,
      hourlyRateCents: 25000,
      estimatedPaymentDay: 10,
      defaultShiftStartTime: '19:00:00',
      defaultEstimatedHours: 12,
      countsAttendances: true,
      createdAt: t,
      updatedAt: t,
    },
  ]
}

export function screenshotFixtureAttendanceTypes(): AttendanceTypeRecord[] {
  const t = nowIso
  return [
    { id: 'att-1', name: 'Particular', description: null, createdAt: t, updatedAt: t },
    { id: 'att-2', name: 'Convênio A', description: 'Plano ouro', createdAt: t, updatedAt: t },
    { id: 'att-3', name: 'Convênio B', description: 'Plano básico', createdAt: t, updatedAt: t },
  ]
}

export function screenshotFixtureCidPage(offset: number, limit: number): {
  items: CidCodeRecord[]
  total: number
  hasMore: boolean
} {
  const all: CidCodeRecord[] = Array.from({ length: 40 }, (_, i) => ({
    id: `cid-${pad(i + 1, 6)}`,
    code: `A0${(i % 9) + 1}`,
    description: ['Hipertensão essencial', 'Diabetes mellitus tipo 2', 'Asma brônquica', 'Gripe'][i % 4]!,
    category: 'Capítulo mock',
    createdAt: nowIso,
    updatedAt: nowIso,
  }))
  const items = all.slice(offset, offset + limit)
  return { items, total: all.length, hasMore: offset + items.length < all.length }
}

export function screenshotFixtureProcedurePage(offset: number, limit: number): {
  items: ProcedureCodeRecord[]
  total: number
  hasMore: boolean
} {
  const all: ProcedureCodeRecord[] = Array.from({ length: 35 }, (_, i) => ({
    id: `proc-${pad(i + 1, 6)}`,
    codeTuss: `${10101012 + i}`,
    codeCbhpm: i % 2 === 0 ? `3.01.01.0${i % 9}` : null,
    description: ['Consulta médica', 'Eletrocardiograma', 'Hemograma', 'Ultrassom abdominal'][i % 4]!,
    category: 'Ambulatorial',
    grupo: 'Grupo A',
    fonte: 'TUSS',
    createdAt: nowIso,
    updatedAt: nowIso,
  }))
  const items = all.slice(offset, offset + limit)
  return { items, total: all.length, hasMore: offset + items.length < all.length }
}

export function screenshotFixtureActiveIngredientsPage(offset: number, limit: number): {
  items: ActiveIngredientRecord[]
  total: number
  hasMore: boolean
} {
  const names = [
    'Losartana potássica',
    'Metformina',
    'Atorvastatina cálcica',
    'Omeprazol',
    'Sinvastatina',
    'AAS',
    'Amlodipina',
    'Levotiroxina',
    'Clonazepam',
    'Paracetamol',
  ]
  const all: ActiveIngredientRecord[] = Array.from({ length: 28 }, (_, i) => ({
    id: `pa-${pad(i + 1, 8)}`,
    ownerUserId: FIXTURE_USER_DOCTOR_ID,
    name: `${names[i % names.length]!} (${i + 1})`,
    indicacao: 'Uso conforme prescrição médica.',
    categoria: 'Cardiovascular',
    usoMagistral: false,
    ...(i === 0
      ? {
          dcbCode: 90001,
          doseAdultaText: '50 mg uma vez ao dia (mock)',
          alternativas: ['Metformina'],
          nomesComerciais: ['Cozaar'],
        }
      : {}),
    createdAt: nowIso,
    updatedAt: nowIso,
  }))
  const items = all.slice(offset, offset + limit)
  return { items, total: all.length, hasMore: offset + items.length < all.length }
}

export function screenshotFixtureAiUsageStatus(): AiUsageStatus {
  return {
    requestsUsed: 12,
    tokensUsed: 8400,
    requestsLimit: 40,
    tokensLimit: 80000,
    periodKey: '2026-04',
  }
}

const emptyFeatures = {} as Json

export function screenshotFixtureAdminUsersAndPlans(): { users: UserLicenseRecord[]; plans: PlanRecord[] } {
  const t = nowIso
  const plans: PlanRecord[] = [
    {
      id: 'free',
      name: 'Free',
      doctorPatientLimit: 30,
      storageLimitMb: 256,
      aiImportDataLimit: 2,
      aiSummarizeLimit: 5,
      aiUsagePeriod: 'week',
      aiRequestsPerDay: 10,
      aiTokensPerDay: 40000,
      features: emptyFeatures,
      createdAt: t,
    },
    {
      id: 'pro',
      name: 'Pro',
      doctorPatientLimit: 500,
      storageLimitMb: 4096,
      aiImportDataLimit: 20,
      aiSummarizeLimit: 100,
      aiUsagePeriod: 'month',
      aiRequestsPerDay: 80,
      aiTokensPerDay: 200000,
      features: emptyFeatures,
      createdAt: t,
    },
  ]
  const users: UserLicenseRecord[] = Array.from({ length: 8 }, (_, i) => ({
    userId: `u-${pad(i + 1, 8)}`,
    displayName: `Médico Mock ${i + 1}`,
    email: `medico${i + 1}@clinica.invalid`,
    role: 'doctor',
    isSystemAdmin: i === 0,
    planId: i % 3 === 0 ? 'pro' : 'free',
    planName: i % 3 === 0 ? 'Pro' : 'Free',
    doctorPatientLimit: 30,
    storageLimitMb: 256,
    aiImportDataLimit: 2,
    aiSummarizeLimit: 5,
    aiUsagePeriod: 'week' as const,
    aiRequestsPerDay: 10,
    aiTokensPerDay: 40000,
    onboardingWelcomeSeen: true,
    onboardingCompleted: true,
    updatedAt: t,
    aiUsageDaily: {
      requestsUsed: 3 + i,
      tokensUsed: 1200 + i * 100,
      requestsLimit: 10,
      tokensLimit: 40000,
    },
  }))
  return { users, plans }
}

export function screenshotFixtureAdminSettings(): { settings?: AdminSettingsRecord } {
  return {
    settings: {
      id: 'adm-settings-1',
      notifyNewUserEmail: 'admin@clinica.invalid',
      helpLinkUrl: 'https://exemplo.invalid/ajuda',
      createdAt: nowIso,
      updatedAt: nowIso,
    },
  }
}
